import { useState, useEffect, useCallback } from 'react';
import { isMediaUnsupportedError } from '../../lib/media-utils';
import { detectBrowserFamily, getMicPermissionRetryHint } from '../../lib/media-permission-recovery';
import { captureException } from '../../lib/telemetry/posthog/frontend';

/**
 * Options for {@link useMediaPermission}.
 */
export interface UseMediaPermissionOptions {
  /**
   * Called with the selected audio input device id whenever it changes
   * (initial default selection, fallback re-selection, or user choice).
   */
  onDeviceSelect: (deviceId: string) => void;
  /**
   * Telemetry context prefix, e.g. 'MicrophoneSetup' or 'MediaSetup'.
   * Used to build the `Component.method` label passed to captureException.
   */
  context: string;
  /**
   * Optional hook run after the mandatory audio permission is granted but
   * before device enumeration. Consumers use this to request a secondary,
   * non-fatal stream (for example video). Throwing here is treated the same
   * as any other acquisition failure and surfaces the permission error UI,
   * so non-fatal failures MUST be swallowed inside the callback.
   */
  onAudioGranted?: () => Promise<void> | void;
  /**
   * Optional hook run after device enumeration completes, receiving the full
   * device list. Consumers use this to pick a secondary device (for example a
   * default camera) without re-enumerating.
   */
  onDevicesEnumerated?: (devices: MediaDeviceInfo[]) => void;
  /**
   * When true, no permission is requested on mount: the prompt is deferred
   * until the consumer calls {@link UseMediaPermissionResult.requestAccess}
   * (typically on a Start gesture). Defaults to false (eager, request on mount).
   */
  deferUntilRequested?: boolean;
}

/**
 * Return shape of {@link useMediaPermission}.
 */
export interface UseMediaPermissionResult {
  /** Available audio input devices, populated once permission is granted. */
  devices: MediaDeviceInfo[];
  /** Currently selected audio input device id. */
  selectedId: string;
  /** Selects an audio input device and notifies the consumer. */
  setSelectedId: (deviceId: string) => void;
  /** True when audio access was hard-denied or otherwise errored (retryable via Enable Access). */
  permissionError: boolean;
  /** True when the user closed the browser prompt without choosing (Chrome "Permission dismissed"). */
  permissionDismissed: boolean;
  /** True when the MediaDevices API is unavailable entirely (RBI / sandboxed iframe). */
  unsupportedEnvironment: boolean;
  /** Browser-specific recovery hint shown after a failed Enable Access retry. */
  retryHint: string | null;
  /** True while an Enable Access retry is in flight. */
  isRetrying: boolean;
  /** True once audio permission has been granted and devices enumerated. */
  granted: boolean;
  /**
   * Requests access on demand (audio plus any secondary stream), classifying
   * failures into the matching UI state. Resolves true when access is granted,
   * false otherwise. No-ops (resolves true) when access was already granted.
   * Intended to be triggered from a user gesture such as the Start button.
   */
  requestAccess: () => Promise<boolean>;
  /** Re-requests audio (plus any secondary stream) and reports diagnostics on failure. */
  handleEnableAccess: () => Promise<void>;
  /** Re-requests access from the dismissed state; re-enters dismissed on a second dismiss. */
  handleRetryFromDismissed: () => Promise<void>;
}

/**
 * Shared permission/device/metering state machine for the lobby media setup
 * surfaces. Owns the mandatory audio permission flow plus audio-input device
 * enumeration, and classifies failures into three distinct UI states:
 * hard denial/error, prompt dismissed, and unsupported environment.
 *
 * Consumers supply optional `onAudioGranted` / `onDevicesEnumerated` hooks to
 * extend the flow (for example video acquisition and camera selection) without
 * forking the error handling.
 */
export function useMediaPermission(
  opts: UseMediaPermissionOptions
): UseMediaPermissionResult {
  const { onDeviceSelect, context, onAudioGranted, onDevicesEnumerated, deferUntilRequested } = opts;

  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedId, setSelectedIdState] = useState<string>('');
  const [permissionError, setPermissionError] = useState(false);
  const [permissionDismissed, setPermissionDismissed] = useState(false);
  const [unsupportedEnvironment, setUnsupportedEnvironment] = useState(false);
  const [retryHint, setRetryHint] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [granted, setGranted] = useState(false);

  const setSelectedId = useCallback(
    (deviceId: string) => {
      setSelectedIdState(deviceId);
      onDeviceSelect(deviceId);
    },
    [onDeviceSelect]
  );

  /**
   * Requests the mandatory audio permission, runs the optional secondary
   * acquisition hook, enumerates audio inputs, and selects a default device.
   * Throws on any failure so callers can classify and surface the right UI.
   */
  const requestPermission = useCallback(async () => {
    // Audio is the critical permission: deny here fails the whole setup.
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((t) => t.stop());

    // Secondary, consumer-owned acquisition (for example video). Any failure
    // it deems non-fatal must be swallowed inside the callback itself.
    if (onAudioGranted) {
      await onAudioGranted();
    }

    const allDevices = await navigator.mediaDevices.enumerateDevices();
    const audioInputs = allDevices.filter((d) => d.kind === 'audioinput');
    setDevices(audioInputs);

    if (audioInputs.length > 0) {
      const defaultDevice =
        audioInputs.find((d) => d.deviceId === 'default') || audioInputs[0];
      setSelectedIdState(defaultDevice.deviceId);
      onDeviceSelect(defaultDevice.deviceId);
    }

    onDevicesEnumerated?.(allDevices);
    setGranted(true);
  }, [onDeviceSelect, onAudioGranted, onDevicesEnumerated]);

  /**
   * Classifies a permission failure into the matching UI state (unsupported,
   * dismissed, or hard error) and reports unexpected errors to PostHog. Always
   * returns false so {@link requestAccess} can `return applyPermissionFailure(e)`.
   */
  const applyPermissionFailure = useCallback((e: unknown): false => {
    // Menlo Security RBI / sandboxed iframes: MediaDevices API absent entirely.
    if (isMediaUnsupportedError(e)) {
      setUnsupportedEnvironment(true);
      return false;
    }

    const err = e as { name?: string; message?: string } | null | undefined;
    // Chrome fires "Permission dismissed" when the user closes the prompt
    // without choosing Allow/Block: retryable, not reported to PostHog.
    const isDismissed =
      (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') &&
      err?.message === 'Permission dismissed';
    if (isDismissed) {
      setPermissionDismissed(true);
      return false;
    }

    console.error('Microphone permission denied or error:', e);
    captureException(
      e instanceof Error ? e : String(e),
      `${context}.requestPermission`,
      {}
    );
    setPermissionError(true);
    return false;
  }, [context]);

  // Initial permission request and device enumeration. Skipped entirely when
  // the consumer opts into deferred mode: the prompt then waits for requestAccess.
  useEffect(() => {
    if (deferUntilRequested) return undefined;
    let cancelled = false;
    const init = async () => {
      try {
        await requestPermission();
        if (cancelled) return;
        setPermissionError(false);
        setRetryHint(null);
      } catch (e: unknown) {
        if (cancelled) return;
        applyPermissionFailure(e);
      }
    };
    void init();
    return () => {
      cancelled = true;
    };
  }, [requestPermission, deferUntilRequested, applyPermissionFailure]);

  const requestAccess = useCallback(async (): Promise<boolean> => {
    // Always re-run the request: an already-granted mic re-resolves without a
    // prompt, and re-running lets consumers re-validate a secondary stream (for
    // example a camera the user has since enabled) on every Start attempt.
    try {
      await requestPermission();
      setPermissionError(false);
      setPermissionDismissed(false);
      setRetryHint(null);
      return true;
    } catch (e: unknown) {
      return applyPermissionFailure(e);
    }
  }, [requestPermission, applyPermissionFailure]);

  const handleEnableAccess = useCallback(async () => {
    setIsRetrying(true);
    const start = Date.now();

    try {
      await requestPermission();
      setPermissionError(false);
      setRetryHint(null);
    } catch (e: unknown) {
      const elapsedMs = Date.now() - start;
      const browser = detectBrowserFamily();
      const err = e as { name?: string; message?: string } | null | undefined;
      const errName = err?.name;
      const errMessage = err?.message;
      const hint = getMicPermissionRetryHint({
        browser,
        errorName: errName,
        elapsedMs,
      });
      setPermissionError(true);
      setRetryHint(hint);
      const isDismissed =
        (errName === 'NotAllowedError' || errName === 'PermissionDeniedError') &&
        errMessage === 'Permission dismissed';
      if (!isDismissed) {
        captureException(
          e instanceof Error ? e : String(e),
          `${context}.retryPermission`,
          { browser, elapsedMs, errorName: errName }
        );
      }
    } finally {
      setIsRetrying(false);
    }
  }, [requestPermission, context]);

  const handleRetryFromDismissed = useCallback(async () => {
    setPermissionDismissed(false);
    try {
      await requestPermission();
      setPermissionError(false);
      setRetryHint(null);
    } catch (e: unknown) {
      const err = e as { name?: string; message?: string } | null | undefined;
      const errName = err?.name;
      const errMessage = err?.message;
      const isDismissed =
        (errName === 'NotAllowedError' || errName === 'PermissionDeniedError') &&
        errMessage === 'Permission dismissed';
      if (isDismissed) {
        setPermissionDismissed(true);
      } else {
        captureException(
          e instanceof Error ? e : String(e),
          `${context}.retryFromDismissed`,
          {}
        );
        setPermissionError(true);
      }
    }
  }, [requestPermission, context]);

  return {
    devices,
    selectedId,
    setSelectedId,
    permissionError,
    permissionDismissed,
    unsupportedEnvironment,
    retryHint,
    isRetrying,
    granted,
    requestAccess,
    handleEnableAccess,
    handleRetryFromDismissed,
  };
}
